/**
 * Keeps credentials out of what any Sentry runtime sends.
 *
 * Sentry's own PII stripping covers `cookie` / `authorization` only when
 * `sendDefaultPii` is off, and nothing it ships knows about our `X-Files-Grant`
 * header, `x-api-key`, or a `?token=` in the URL of an outbound call
 * (an http breadcrumb records `url`; an exception message can quote one).
 * This redacts them from the event (request headers/cookies/URL, breadcrumbs,
 * exception text, extra and contexts) and from each breadcrumb as it is added.
 *
 * Pure (no Sentry import) so it is testable and shared by the server, edge and
 * browser configs.
 */
import {
  isSensitiveKey,
  redactSensitive,
  scrubSensitiveString
} from '@/utils/safe-error-log'

interface ScrubbableBreadcrumb {
  message?: string
  data?: Record<string, unknown>
}

interface ScrubbableEvent {
  message?: string
  request?: {
    url?: string
    headers?: Record<string, unknown>
    cookies?: unknown
    query_string?: unknown
    data?: unknown
  }
  exception?: { values?: { value?: string }[] }
  breadcrumbs?: ScrubbableBreadcrumb[]
  spans?: { description?: string; data?: Record<string, unknown> }[]
  extra?: Record<string, unknown>
  contexts?: Record<string, unknown>
}

const REDACTED = '[REDACTED]'

export function scrubBreadcrumb<B extends ScrubbableBreadcrumb>(crumb: B): B {
  if (crumb.message) crumb.message = scrubSensitiveString(crumb.message)
  if (crumb.data) {
    crumb.data = redactSensitive(crumb.data) as Record<string, unknown>
  }
  return crumb
}

function scrubRequest(request: NonNullable<ScrubbableEvent['request']>): void {
  for (const key of Object.keys(request.headers ?? {})) {
    if (isSensitiveKey(key)) request.headers![key] = REDACTED
  }
  delete request.cookies
  if (request.url) request.url = scrubSensitiveString(request.url)
  if (typeof request.query_string === 'string') {
    request.query_string = scrubSensitiveString(
      `?${request.query_string}`
    ).slice(1)
  } else if (request.query_string) {
    request.query_string = redactSensitive(request.query_string)
  }
  if (request.data !== undefined) request.data = redactSensitive(request.data)
}

function scrubSpans(spans: NonNullable<ScrubbableEvent['spans']>): void {
  for (const span of spans) {
    if (span.description) {
      span.description = scrubSensitiveString(span.description)
    }
    if (span.data) {
      span.data = redactSensitive(span.data) as Record<string, unknown>
    }
  }
}

export function scrubCredentials<E extends ScrubbableEvent>(event: E): E {
  if (event.request) scrubRequest(event.request)
  if (event.message) event.message = scrubSensitiveString(event.message)
  for (const value of event.exception?.values ?? []) {
    if (value.value) value.value = scrubSensitiveString(value.value)
  }
  for (const crumb of event.breadcrumbs ?? []) scrubBreadcrumb(crumb)
  scrubSpans(event.spans ?? [])
  if (event.extra) {
    event.extra = redactSensitive(event.extra) as Record<string, unknown>
  }
  if (event.contexts) {
    event.contexts = redactSensitive(event.contexts) as Record<string, unknown>
  }
  return event
}
